import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ArenaParticipant } from '@/hooks/useLiveArenas';

export interface SpotlightState {
  spotlightedUserId: string | null;
  spotlightedParticipant: ArenaParticipant | null;
}

export const useArenaSpotlight = (arenaId: string | null, participants: ArenaParticipant[]) => {
  const [spotlightedUserId, setSpotlightedUserId] = useState<string | null>(null);

  // Subscribe to spotlight changes via Supabase Realtime
  useEffect(() => {
    if (!arenaId) return;

    const channel = supabase.channel(`arena-spotlight-${arenaId}`);

    channel
      .on('broadcast', { event: 'spotlight' }, (payload) => {
        setSpotlightedUserId(payload.payload.userId || null);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [arenaId]);

  // Set spotlight on a user (host only)
  const setSpotlight = useCallback(
    async (userId: string | null) => {
      if (!arenaId) return;

      const channel = supabase.channel(`arena-spotlight-${arenaId}`);

      await channel.send({
        type: 'broadcast',
        event: 'spotlight',
        payload: {
          userId,
        },
      });

      setSpotlightedUserId(userId);
    },
    [arenaId]
  );

  // Toggle spotlight for a user
  const toggleSpotlight = useCallback(
    async (userId: string) => {
      if (spotlightedUserId === userId) {
        await setSpotlight(null);
      } else {
        await setSpotlight(userId);
      }
    },
    [spotlightedUserId, setSpotlight]
  );

  // Get the spotlighted participant
  const spotlightedParticipant = spotlightedUserId
    ? participants.find((p) => p.user_id === spotlightedUserId) || null
    : null;

  return {
    spotlightedUserId,
    spotlightedParticipant,
    setSpotlight,
    toggleSpotlight,
    isSpotlighted: (userId: string) => spotlightedUserId === userId,
  };
};
